<script setup lang="ts">
import type { DirtyFieldLabels } from "./dialogStack";
import { createReusableTemplate } from "@vueuse/core";
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { computed, nextTick, onUnmounted, provide, ref, shallowRef, useId, watch } from "vue";
import { useDialogDrag } from "../../composables/Dialog/useDialogDrag";
import { tryUseDialogStack } from "../../composables/Dialog/useDialogStack";
import DialogClose from "./DialogClose.vue";
import { DIALOG_FOCUS_CONTAINERS_KEY } from "./dialogFocusContainers";
import { createDialogLabels, DIALOG_LABELS_KEY } from "./dialogLabels";
import { resolveDialogHeight, resolveDialogSize, resolveDialogWidth } from "./dialogSizes";
import { DIALOG_LAYER_KEY, MAX_DIALOG_DEPTH, shouldRenderHostClose } from "./dialogStack";

interface Props {
  id?: string
  size?: string
  /** Exact width in px instead of the width of `size`. Shrinks to the viewport; phones keep the size's sheet. */
  width?: number
  height?: string
  teleportTarget?: HTMLElement | "body" | "self"
  square?: boolean
  confirmClose?: boolean
  isDirty?: () => boolean
  mask?: boolean
  headless?: boolean
  newspaper?: boolean
  draggable?: boolean
  closable?: boolean
  /** A confirm that asks for a decision: `alertdialog` role, 18px corners and the 32/28px confirm padding. */
  alert?: boolean
  /**
   * Temporarily refuses every close gesture (X, Escape, mask) while keeping the close button in
   * place, dimmed. For work that must not be interrupted mid-flight. `closable: false` differs:
   * it removes the affordance altogether.
   */
  closeDisabled?: boolean
  paddingless?: boolean
  /**
   * Hairlines that separate chrome from a scrolling body.
   * `true` draws both. `"footer"` keeps the header gap without a second line under `VSteps`.
   */
  dividers?: boolean | "header" | "footer"
  keepInViewport?: boolean
  header?: string | null
  footer?: string | null
  variant?: "center" | "drawer"
  priority?: "normal" | "system"
  hosted?: boolean
  instanceId?: string
}

const {
  id = undefined,
  size = "medium",
  width = undefined,
  height = "hug",
  teleportTarget = "body",
  square = false,
  confirmClose = false,
  isDirty = undefined,
  mask = true,
  headless = false,
  newspaper = false,
  draggable = false,
  closable = true,
  alert = false,
  closeDisabled = false,
  keepInViewport = true,
  paddingless = false,
  dividers = false,
  header = null,
  footer = null,
  variant = "center",
  priority = "normal",
  hosted = false,
  instanceId: hostedInstanceId = undefined,
} = defineProps<Props>();

const emit = defineEmits<{
  close: []
  confirm: []
  dragstart: [event: MouseEvent]
  dragend: [event: MouseEvent]
}>();
const headerDivider = computed(() => dividers === true || dividers === "header");
const footerDivider = computed(() => dividers === true || dividers === "footer");

const open = defineModel<boolean>("open", { default: false });

const stack = tryUseDialogStack();
const resolvedSize = computed(() => resolveDialogSize(size));
const resolvedWidth = computed(() => resolveDialogWidth(width));
const resolvedHeight = computed(() => resolveDialogHeight(height));
const dialog = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const isClosing = ref(false);
const localInstanceId = ref(hostedInstanceId ?? "");

const isLeaving = computed(() => {
  if (!stack || !localInstanceId.value) {
    return false;
  }
  return Boolean(stack.get(localInstanceId.value)?.leaving);
});

const isOpen = computed({
  get: () => {
    if (isLeaving.value) {
      return false;
    }
    return hosted || open.value;
  },
  set: (value: boolean) => {
    open.value = value;
  },
});

const drawsLocalMask = computed(() => !stack);

const shouldFadeMask = computed(() => {
  if (!stack || !localInstanceId.value) {
    return true;
  }
  const othersActive = stack.layers.some(layer => (
    layer.mask && !layer.leaving && layer.instanceId !== localInstanceId.value
  ));
  const othersLeaving = stack.layers.some(layer => (
    layer.mask && layer.leaving && layer.instanceId !== localInstanceId.value
  ));
  return !othersActive && !othersLeaving;
});

const dialogZIndex = computed(() => {
  if (!stack || !localInstanceId.value) {
    return 1000;
  }
  return stack.get(localInstanceId.value)?.zIndex ?? 1000;
});

const isTop = computed(() => {
  if (!stack || !localInstanceId.value) {
    return isOpen.value;
  }
  return stack.isTop(localInstanceId.value);
});

const depth = computed(() => {
  if (!stack || !localInstanceId.value) {
    return 0;
  }
  const self = stack.get(localInstanceId.value);
  if (!self) {
    return 0;
  }
  const above = stack.layers.filter(layer => (
    layer.mask && !layer.leaving && layer.zIndex > self.zIndex && layer.size !== "confirm"
  )).length;
  return Math.min(above, MAX_DIALOG_DEPTH);
});

const dialogStyle = computed(() => {
  const style: Record<string, string> = { "--dialog-depth": String(depth.value) };
  if (resolvedWidth.value !== undefined) {
    style["--dialog-width"] = `${resolvedWidth.value}px`;
  }
  return style;
});

const isMaskOwner = computed(() => {
  if (!isOpen.value || !mask) {
    return false;
  }
  if (!stack || !localInstanceId.value) {
    return true;
  }
  return stack.maskLayer.value?.instanceId === localInstanceId.value;
});

const showHostClose = computed(() => {
  if (!closable) {
    return false;
  }
  if (!hosted) {
    return true;
  }
  if (!stack || !localInstanceId.value) {
    return false;
  }
  const layer = stack.get(localInstanceId.value);
  return Boolean(layer && shouldRenderHostClose(layer));
});

const maskZIndex = computed(() => dialogZIndex.value - 1);

const layerApi = {
  get id() {
    return id ?? localInstanceId.value;
  },
  get instanceId() {
    return localInstanceId.value;
  },
  get paddingless() {
    return paddingless;
  },
  close: () => closeThisDialog(false),
  closeAfterSave: () => closeThisDialog(true),
  setDirty: (dirty: boolean) => {
    if (stack && localInstanceId.value) {
      stack.setDirty(localInstanceId.value, dirty);
    }
  },
  setDirtyFieldLabels: (fn: (() => DirtyFieldLabels) | undefined) => {
    if (stack && localInstanceId.value) {
      stack.setDirtyFieldLabels(localInstanceId.value, fn);
    }
  },
  setLocked: (locked: boolean) => {
    if (stack && localInstanceId.value) {
      stack.setLocked(localInstanceId.value, locked);
    }
  },
  setOnLocked: (fn: (() => void) | undefined) => {
    if (stack && localInstanceId.value) {
      stack.setOnLocked(localInstanceId.value, fn);
    }
  },
};

// Headless dialogs have no shell title: their content (DialogTitle, VDialogHeader, the confirm bodies) names them.
const labels = createDialogLabels(`dialog-${useId()}`, () => headless);
provide(DIALOG_LABELS_KEY, labels);

// A declarative shell nested inside another layer's content must still be its own layer, otherwise
// its host close (and any chrome in its slots) would resolve to the parent and close that instead.
if (!hosted) {
  provide(DIALOG_LAYER_KEY, layerApi);
}

watch(() => isDirty?.() ?? false, (dirty) => {
  layerApi.setDirty(Boolean(dirty));
});

// Only a change of the prop drives the stack lock, so a hosted layer whose owner locks it through
// the stack is not unlocked by the prop's default on mount.
watch(() => closeDisabled, (locked, wasLocked) => {
  if (locked === Boolean(wasLocked)) {
    return;
  }
  layerApi.setLocked(locked);
}, { immediate: true });

function registerDeclarative() {
  if (!stack || hosted) {
    return;
  }
  localInstanceId.value = stack.push({
    id,
    source: "declarative",
    variant,
    size: resolvedSize.value,
    width: resolvedWidth.value,
    height: resolvedHeight.value,
    priority,
    mask,
    paddingless,
    dismissible: closable && !closeDisabled,
    alert,
    headless,
    header,
    footer,
    dirty: isDirty,
    onClose: () => {
      isOpen.value = false;
      emit("close");
    },
  });
}

function unregisterDeclarative(force = false) {
  if (!stack || hosted || !localInstanceId.value) {
    return Promise.resolve(true);
  }
  return force
    ? stack.popAfterSave(localInstanceId.value)
    : stack.pop(localInstanceId.value);
}

watch(isOpen, (value) => {
  if (hosted) {
    if (hostedInstanceId) {
      localInstanceId.value = hostedInstanceId;
    }
    return;
  }
  if (value) {
    registerDeclarative();
    return;
  }
  if (isLeaving.value || !localInstanceId.value) {
    return;
  }
  void unregisterDeclarative(true);
}, { immediate: true });

watch(localInstanceId, (instanceId, previousId) => {
  if (!stack) {
    return;
  }
  if (previousId) {
    stack.unregisterLeaveWaiter(previousId);
  }
  if (instanceId) {
    stack.registerLeaveWaiter(instanceId);
  }
}, { immediate: true });

onUnmounted(() => {
  if (stack && localInstanceId.value) {
    stack.finishLeave(localInstanceId.value);
    stack.unregisterLeaveWaiter(localInstanceId.value);
  }
  if (!hosted && localInstanceId.value) {
    void unregisterDeclarative(true);
  }
});

function onDialogAfterLeave() {
  if (stack && localInstanceId.value) {
    stack.finishLeave(localInstanceId.value);
  }
}

// Panels this dialog's content teleports elsewhere (floating-vue poppers under `body`) share its focus trap.
const extraFocusContainers = shallowRef<HTMLElement[]>([]);
provide(DIALOG_FOCUS_CONTAINERS_KEY, {
  add: (element) => {
    if (!extraFocusContainers.value.includes(element)) {
      extraFocusContainers.value = [...extraFocusContainers.value, element];
    }
  },
  remove: (element) => {
    extraFocusContainers.value = extraFocusContainers.value.filter(container => container !== element);
  },
});

const focusTrapContainers = computed(() => (dialog.value ? [dialog.value, ...extraFocusContainers.value] : []));

// focus-trap pauses this trap while another one (an inner dialog) is active on top of it.
const focusTrapPaused = ref(false);

const { activate, deactivate, hasFocus: focusTrapHasFocus } = useFocusTrap(focusTrapContainers, {
  clickOutsideDeactivates: true,
  escapeDeactivates: false,
  allowOutsideClick: true,
  returnFocusOnDeactivate: true,
  initialFocus: false,
  fallbackFocus: () => dialog.value as HTMLElement,
  onPostPause: () => {
    focusTrapPaused.value = true;
  },
  onPostUnpause: () => {
    focusTrapPaused.value = false;
  },
  onDeactivate: () => {
    focusTrapPaused.value = false;
  },
});

// Marks the dialog element for content that must keep Tab inside it (VModernSelect).
const focusTrapActive = computed(() => focusTrapHasFocus.value && !focusTrapPaused.value);

watch(
  () => [isOpen.value, isTop.value] as const,
  ([opened, top]) => {
    if (opened && top) {
      nextTick(() => {
        setTimeout(() => {
          try {
            if (dialog.value && document.contains(dialog.value)) {
              activate();
            }
          } catch {
            // Focus trap can fail while content is still mounting.
          }
        }, 50);
      });
    } else {
      try {
        deactivate();
      } catch {
        // already deactivated
      }
    }
  },
  { immediate: true },
);

const dialogElement = computed(() => dialog.value as HTMLElement | null);

const { initDrag } = useDialogDrag(dialogElement, {
  draggable,
  keepInViewport,
  onDragStart: (event) => {
    isDragging.value = true;
    emit("dragstart", event);
  },
  onDragEnd: (event) => {
    isDragging.value = false;
    emit("dragend", event);
  },
});

async function closeThisDialog(force = false): Promise<boolean> {
  if (isClosing.value || !closable) {
    return false;
  }
  if (closeDisabled && !force) {
    return false;
  }
  isClosing.value = true;
  try {
    if (!force && confirmClose && isDirty?.() && stack) {
      const confirmed = await stack.pop(localInstanceId.value);
      if (confirmed) {
        isOpen.value = false;
        emit("close");
      }
      return confirmed;
    }
    if (stack && localInstanceId.value) {
      const closed = force
        ? await stack.popAfterSave(localInstanceId.value)
        : await stack.pop(localInstanceId.value);
      if (closed) {
        isOpen.value = false;
        emit("close");
      }
      return closed;
    }
    isOpen.value = false;
    emit("close");
    return true;
  } finally {
    isClosing.value = false;
  }
}

function handleMaskClick() {
  if (closeDisabled) {
    return;
  }
  if (!stack) {
    void closeThisDialog();
    return;
  }
  const top = stack.top.value;
  if (!top || !top.dismissible) {
    return;
  }
  void stack.pop(top.instanceId);
}

function handleDialogClick(event: MouseEvent) {
  event.stopPropagation();
}

// The event is optional so slot consumers can request a close programmatically (and still get the
// confirm-close guard) instead of reaching past it to their own hide().
function handleCloseClick(event?: MouseEvent) {
  event?.stopPropagation();
  if (closeDisabled) {
    return;
  }
  void closeThisDialog();
}

function bringDialogToFront() {
  if (stack && localInstanceId.value) {
    stack.bringToFront(localInstanceId.value);
  }
}

// Names the dialog for assistive tech off whatever the header renders, so the announced name is the
// visible title rather than the generic "dialog" role. Instance-scoped: the same `id` can be mounted
// more than once (nested/stacked dialogs).
const titleId = computed(() => `dialog-title-${localInstanceId.value || id}`);

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();
</script>

<template>
  <DefineTemplate>
    <Transition v-if="drawsLocalMask" :name="shouldFadeMask ? 'fade' : ''" appear>
      <div
        v-if="isMaskOwner"
        class="mask"
        :style="{ zIndex: maskZIndex }"
        @click="handleMaskClick"
        @mousedown.stop
      />
    </Transition>
    <Transition
      :name="newspaper ? 'newspaper' : 'dialog'"
      appear
      @after-leave="onDialogAfterLeave"
    >
      <div
        v-if="isOpen"
        class="dialog-wrapper"
        :class="[variant]"
        :style="{ zIndex: dialogZIndex }"
        @mousedown="bringDialogToFront"
      >
        <div
          ref="dialog"
          :data-instance-id="localInstanceId"
          :data-dialog-id="id"
          class="dialog"
          :class="[
            resolvedSize,
            variant,
            {
              square,
              headless,
              alert,
              'tall': resolvedHeight === 'tall',
              'custom-width': resolvedWidth !== undefined,
              'dividers-header': headerDivider,
              'dividers-footer': footerDivider,
              'draggable': draggable && !headless,
              'no-padding': paddingless,
              'has-host-close': showHostClose,
            },
          ]"
          :style="dialogStyle"
          :role="alert ? 'alertdialog' : 'dialog'"
          :aria-modal="mask ? 'true' : undefined"
          :data-focus-trap="focusTrapActive ? 'active' : undefined"
          :aria-labelledby="headless ? labels.titleId.value : titleId"
          :aria-describedby="labels.descriptionId.value"
          tabindex="-1"
          @click="handleDialogClick"
          @mousedown.stop
        >
          <div
            v-if="!headless"
            class="header"
            @mousedown.prevent="initDrag"
          >
            <div :id="titleId" class="title-container">
              <slot name="header" :close="handleCloseClick">
                <span v-if="header" class="title">{{ header }}</span>
                <slot v-else name="title" :close="handleCloseClick" />
              </slot>
            </div>
          </div>
          <div class="body">
            <slot name="content" :close="handleCloseClick">
              <slot :close="handleCloseClick" />
            </slot>
          </div>
          <DialogClose v-if="showHostClose" :class="closeDisabled ? 'opacity-40 pointer-events-none' : undefined" />
          <div v-if="$slots.footer || footer" class="footer">
            <slot name="footer" :close="handleCloseClick">
              {{ footer }}
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </DefineTemplate>
  <template v-if="teleportTarget === 'self'">
    <ReuseTemplate />
  </template>
  <Teleport v-else :to="teleportTarget">
    <ReuseTemplate />
  </Teleport>
</template>

<style scoped lang="scss">
@use "./Dialog";
</style>
